/**
 * Admin Automations — two tabs:
 *   1. Platform Automations  — global rules on platform-scoped triggers
 *      (new tenant signup, subscription expiring, wallet low, ecourts errors,
 *       founder milestones …). Stored in `platform_automations` collection.
 *   2. Tenant Automations    — configure a specific tenant's rules ON THEIR
 *      BEHALF. Useful when a tenant doesn't know how to build their own.
 *      Every write is stamped `configured_by_super_admin` so the tenant can
 *      see who set it up.
 *
 * v1 delivers CRUD + list/enable/disable/delete. The executor scheduler is
 * v1.2 (parallel to the tenant-side promise in routes_automation.py).
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Users, Plus, Play, Trash2, Save } from "lucide-react";

const TABS = [
    { id: "platform", label: "Platform Rules", icon: Zap },
    { id: "tenant",   label: "Configure a Tenant", icon: Users },
];

export default function AdminAutomations() {
    const [tab, setTab] = useState("platform");
    return (
        <div className="space-y-6" data-testid="admin-automations">
            <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Automations</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Build platform-wide rules (new signups, expiring subs, low wallets) or
                    configure a specific tenant&apos;s automations on their behalf.
                </p>
            </div>
            <div className="flex gap-1 border-b border-border overflow-x-auto scrollbar-hide" data-testid="admin-automations-tabs">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        data-testid={`admin-auto-tab-${t.id}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                            tab === t.id
                                ? "border-primary text-primary"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}>
                        <t.icon className="w-4 h-4"/> {t.label}
                    </button>
                ))}
            </div>
            {tab === "platform" && <PlatformAutomations/>}
            {tab === "tenant"   && <TenantAutomationConfigurator/>}
        </div>
    );
}


function PlatformAutomations() {
    const [items, setItems] = useState([]);
    const [catalog, setCatalog] = useState({ triggers: [], actions: [] });
    const [placeholders, setPlaceholders] = useState({});
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null); // full rule object being edited
    const [seeding, setSeeding] = useState(false);
    const load = useCallback(async () => {
        try {
            const [a, c, p] = await Promise.all([
                api.get("/admin/platform-automations"),
                api.get("/admin/platform-automations/catalog"),
                api.get("/admin/automations/placeholders").catch(() => ({ data: { catalog: {} } })),
            ]);
            setItems(a.data); setCatalog(c.data);
            setPlaceholders(p.data.catalog || {});
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggle = async (a) => {
        try {
            await api.patch(`/admin/platform-automations/${a.id}`, { enabled: !a.enabled });
            toast.success(`${a.name} ${!a.enabled ? "enabled" : "disabled"}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };
    const del = async (a) => {
        if (!window.confirm(`Delete "${a.name}"?`)) return;
        try { await api.delete(`/admin/platform-automations/${a.id}`); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const seedDefaults = async () => {
        if (!window.confirm("Seed 10 default platform automations? Existing rules with matching slugs are skipped.")) return;
        setSeeding(true);
        try {
            const { data } = await api.post("/admin/platform-automations/seed-defaults");
            const patchedMsg = data.patched > 0 ? ` · ${data.patched} rule(s) updated` : "";
            toast.success(`Seeded ${data.created} new rule(s) · ${data.total - data.created} already existed${patchedMsg}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSeeding(false); }
    };

    return (
        <div className="space-y-4" data-testid="platform-automations">
            <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">{items.length} platform rule(s)</div>
                <div className="flex items-center gap-2">
                    <button onClick={seedDefaults} disabled={seeding}
                        data-testid="platform-auto-seed-btn"
                        title="Creates missing rules and patches empty messages on existing ones"
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-border text-muted-foreground hover:text-foreground rounded-lg text-xs uppercase tracking-[0.14em] disabled:opacity-50">
                        {seeding ? "Seeding…" : "Seed / update defaults"}
                    </button>
                    <button onClick={() => setCreating(true)}
                        data-testid="platform-auto-new-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs uppercase tracking-[0.14em]">
                        <Plus className="w-3.5 h-3.5"/> New rule
                    </button>
                </div>
            </div>
            {items.length === 0 ? (
                <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center text-sm text-muted-foreground"
                    data-testid="platform-auto-empty">
                    No platform rules yet. Click <b>Seed defaults</b> to load 10 starter rules, or <b>New rule</b> to build your own.
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map(a => (
                        <AutomationRow key={a.id} a={a}
                            onToggle={() => toggle(a)}
                            onEdit={() => setEditing(a)}
                            onDelete={() => del(a)}
                            testidPrefix={`platform-auto-${a.id}`}/>
                    ))}
                </div>
            )}
            {(creating || editing) && (
                <AutomationBuilder catalog={catalog} placeholders={placeholders}
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSave={async (payload) => {
                        try {
                            if (editing) {
                                await api.patch(`/admin/platform-automations/${editing.id}`, payload);
                                toast.success("Rule updated");
                            } else {
                                await api.post("/admin/platform-automations", payload);
                                toast.success("Platform rule created");
                            }
                            setCreating(false); setEditing(null); load();
                        } catch (e) { toast.error(apiError(e)); }
                    }}
                    scope="platform"/>
            )}
        </div>
    );
}


function TenantAutomationConfigurator() {
    const [tenants, setTenants] = useState([]);
    const [tenantId, setTenantId] = useState("");
    const [tenantData, setTenantData] = useState(null);
    const [catalog, setCatalog] = useState({ triggers: [], actions: [] });
    const [placeholders, setPlaceholders] = useState({});
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [query, setQuery] = useState("");

    useEffect(() => {
        Promise.all([
            api.get("/tenants"),
            api.get("/admin/automations/placeholders").catch(() => ({ data: { catalog: {} } })),
        ]).then(([t, p]) => {
            setTenants(t.data || []);
            setPlaceholders(p.data.catalog || {});
        }).catch(e => toast.error(apiError(e)));
    }, []);

    const loadTenant = useCallback(async (tid) => {
        if (!tid) { setTenantData(null); return; }
        try {
            const [a, c] = await Promise.all([
                api.get(`/admin/tenants/${tid}/automations`),
                api.get(`/admin/tenants/${tid}/automations/catalog`),
            ]);
            setTenantData(a.data); setCatalog(c.data);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { loadTenant(tenantId); }, [tenantId, loadTenant]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return tenants;
        return tenants.filter(t => (t.name || "").toLowerCase().includes(q)
                                    || (t.slug || "").toLowerCase().includes(q));
    }, [tenants, query]);

    const toggle = async (a) => {
        try {
            await api.patch(`/admin/tenants/${tenantId}/automations/${a.id}`, { enabled: !a.enabled });
            toast.success(`${a.name} ${!a.enabled ? "enabled" : "disabled"}`);
            loadTenant(tenantId);
        } catch (e) { toast.error(apiError(e)); }
    };
    const del = async (a) => {
        if (!window.confirm(`Delete "${a.name}"?`)) return;
        try { await api.delete(`/admin/tenants/${tenantId}/automations/${a.id}`); loadTenant(tenantId); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-4" data-testid="tenant-automation-configurator">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input type="text" placeholder="Search tenants…" value={query}
                    onChange={e => setQuery(e.target.value)}
                    data-testid="tenant-auto-search"
                    className="bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm rounded-lg text-foreground"/>
                <select value={tenantId} onChange={e => setTenantId(e.target.value)}
                    data-testid="tenant-auto-picker"
                    className="bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm rounded-lg text-foreground">
                    <option value="">— Pick a tenant —</option>
                    {filtered.map(t => (
                        <option key={t.id} value={t.id}>{t.name} · {t.slug}</option>
                    ))}
                </select>
            </div>
            {tenantData && (
                <>
                    <div className="flex items-center justify-between border-b border-border pb-3">
                        <div className="text-sm text-foreground">
                            <span className="text-muted-foreground text-xs uppercase tracking-[0.14em] mr-2">Configuring for</span>
                            <span className="font-medium">{tenantData.tenant_name}</span>
                        </div>
                        <button onClick={() => setCreating(true)}
                            data-testid="tenant-auto-new-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs uppercase tracking-[0.14em]">
                            <Plus className="w-3.5 h-3.5"/> New rule
                        </button>
                    </div>
                    {tenantData.automations.length === 0 ? (
                        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center text-sm text-muted-foreground"
                            data-testid="tenant-auto-empty">
                            This tenant has no automations. Click <b>New rule</b> to configure one on their behalf.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {tenantData.automations.map(a => (
                                <AutomationRow key={a.id} a={a}
                                    onToggle={() => toggle(a)}
                                    onEdit={() => setEditing(a)}
                                    onDelete={() => del(a)}
                                    testidPrefix={`tenant-auto-${a.id}`} showSuperAdminBadge/>
                            ))}
                        </div>
                    )}
                </>
            )}
            {(creating || editing) && (
                <AutomationBuilder catalog={catalog} placeholders={placeholders}
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSave={async (payload) => {
                        try {
                            if (editing) {
                                await api.patch(`/admin/tenants/${tenantId}/automations/${editing.id}`, payload);
                                toast.success("Rule updated");
                            } else {
                                await api.post(`/admin/tenants/${tenantId}/automations`, payload);
                                toast.success("Rule created for tenant");
                            }
                            setCreating(false); setEditing(null); loadTenant(tenantId);
                        } catch (e) { toast.error(apiError(e)); }
                    }}
                    scope="tenant" tenantName={tenantData?.tenant_name}/>
            )}
        </div>
    );
}


function AutomationRow({ a, onToggle, onEdit, onDelete, testidPrefix, showSuperAdminBadge }) {
    return (
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3"
            data-testid={testidPrefix}>
            <div className="flex-1">
                <div className="flex items-center gap-2">
                    <div className="font-medium text-sm text-foreground">{a.name}</div>
                    {a.enabled ? (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-emerald-500 border border-emerald-500/40 px-1.5 py-0.5 rounded">Live</span>
                    ) : (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border border-border px-1.5 py-0.5 rounded">Draft</span>
                    )}
                    {showSuperAdminBadge && a.configured_by_super_admin && (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-primary border border-primary/40 px-1.5 py-0.5 rounded"
                            data-testid={`${testidPrefix}-super-admin-badge`}>Set up by Law OS</span>
                    )}
                    {a.seeded_default && (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-sky-500 border border-sky-500/40 px-1.5 py-0.5 rounded">Seeded default rule</span>
                    )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                    Trigger: <code className="text-foreground">{a.trigger?.type || "—"}</code> · Actions: {(a.actions || []).length}
                    {a.description && <> · {a.description}</>}
                </div>
            </div>
            <button onClick={onToggle} data-testid={`${testidPrefix}-toggle`}
                className={`text-xs uppercase tracking-[0.14em] px-3 py-1.5 rounded-lg border ${
                    a.enabled ? "text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10" : "text-muted-foreground border-border hover:text-foreground"
                }`}>
                {a.enabled ? "Disable" : "Enable"}
            </button>
            <button onClick={onEdit} data-testid={`${testidPrefix}-edit`}
                className="text-xs text-primary hover:bg-primary/10 rounded-lg px-3 py-1.5 border border-primary/30">
                Edit
            </button>
            <button onClick={onDelete} data-testid={`${testidPrefix}-delete`}
                className="text-xs text-rose-500 hover:bg-rose-500/10 rounded-lg p-2">
                <Trash2 className="w-3.5 h-3.5"/>
            </button>
        </div>
    );
}


function AutomationBuilder({ catalog, placeholders, initial, onClose, onSave, scope, tenantName }) {
    const isEdit = !!initial;
    const [name, setName] = useState(initial?.name || "");
    const [description, setDescription] = useState(initial?.description || "");
    const [triggerType, setTriggerType] = useState(initial?.trigger?.type || (catalog.triggers?.[0]?.type) || "");
    const [triggerParams, setTriggerParams] = useState(() => {
        if (!initial?.trigger) return {};
        const { type, ...rest } = initial.trigger;
        return rest;
    });
    const [actions, setActions] = useState(initial?.actions || []);
    const [enabled, setEnabled] = useState(initial?.enabled || false);
    // Track which text field the placeholder pill should insert into.
    // key format: `action-${i}-${field.key}` OR `trigger-${field.key}`.
    const [focusedField, setFocusedField] = useState(null);
    const trigDef = catalog.triggers?.find(t => t.type === triggerType);
    const placeholderGroups = Object.keys(placeholders || {});

    const insertPlaceholder = (token) => {
        if (!focusedField) { toast.info("Click into a message/subject field first"); return; }
        if (focusedField.startsWith("action-")) {
            const [, i, key] = focusedField.split("-");
            setActions(prev => prev.map((a, j) => j === Number(i)
                ? { ...a, [key]: `${a[key] || ""}${token}` } : a));
        } else if (focusedField.startsWith("trigger-")) {
            const key = focusedField.replace("trigger-", "");
            setTriggerParams(p => ({ ...p, [key]: `${p[key] || ""}${token}` }));
        }
    };

    const addAction = () => setActions(prev => [...prev, { type: catalog.actions[0]?.type }]);
    const updateAction = (i, field, value) => setActions(prev => prev.map((a, j) => j === i ? { ...a, [field]: value } : a));
    const removeAction = (i) => setActions(prev => prev.filter((_, j) => j !== i));

    const submit = async () => {
        if (!name.trim()) return toast.error("Name is required");
        if (!triggerType) return toast.error("Pick a trigger");
        if (actions.length === 0) return toast.error("Add at least one action");
        await onSave({
            name, description,
            trigger: { type: triggerType, ...triggerParams },
            actions, enabled,
        });
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose} data-testid="automation-builder-modal">
            <div className="bg-background border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-border">
                    <div className="text-lg font-heading text-foreground">
                        {isEdit ? "Edit" : "New"} {scope === "platform" ? "platform" : "tenant"} rule
                        {tenantName && <span className="text-muted-foreground text-sm ml-2">· for {tenantName}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {scope === "platform"
                            ? "Fires on platform-scoped events (new signup, subscription expiring, wallet low, …)"
                            : "Configures an automation inside this tenant's namespace. Every write is stamped so the tenant sees who set it up."}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Name</label>
                        <input value={name} onChange={e => setName(e.target.value)}
                            data-testid="builder-name"
                            placeholder="e.g. Notify me when a founder signs up"
                            className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 rounded-lg text-foreground"/>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Description (optional)</label>
                        <input value={description} onChange={e => setDescription(e.target.value)}
                            data-testid="builder-description"
                            className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 rounded-lg text-foreground"/>
                    </div>
                    <div className="border-t border-border pt-4">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-primary">When (trigger)</label>
                        <select value={triggerType} onChange={e => { setTriggerType(e.target.value); setTriggerParams({}); }}
                            data-testid="builder-trigger"
                            className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 rounded-lg text-foreground">
                            {(catalog.triggers || []).map(t => (
                                <option key={t.type} value={t.type}>{t.label}</option>
                            ))}
                        </select>
                        {(trigDef?.fields || []).map(f => (
                            <div key={f.key} className="mt-2">
                                <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{f.label}</label>
                                <input type={f.type === "number" ? "number" : "text"}
                                    value={triggerParams[f.key] ?? f.default ?? ""}
                                    onFocus={() => setFocusedField(`trigger-${f.key}`)}
                                    onChange={e => setTriggerParams(p => ({ ...p, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value }))}
                                    className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 rounded-lg text-foreground"/>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-border pt-4">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] uppercase tracking-[0.14em] text-primary">Then (actions)</label>
                            <button onClick={addAction} data-testid="builder-add-action"
                                className="text-[11px] text-primary hover:underline">+ Add action</button>
                        </div>
                        {actions.length === 0 ? (
                            <div className="mt-2 text-xs text-muted-foreground">No actions yet.</div>
                        ) : actions.map((act, i) => {
                            const actDef = catalog.actions?.find(a => a.type === act.type);
                            return (
                                <div key={i} className="mt-3 border border-border rounded-lg p-3 space-y-2"
                                    data-testid={`builder-action-${i}`}>
                                    <div className="flex items-center gap-2">
                                        <select value={act.type} onChange={e => updateAction(i, "type", e.target.value)}
                                            className="flex-1 bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-2 py-1.5 rounded-md text-foreground text-xs">
                                            {(catalog.actions || []).map(a => (
                                                <option key={a.type} value={a.type}>{a.label}</option>
                                            ))}
                                        </select>
                                        <button onClick={() => removeAction(i)}
                                            className="text-[11px] text-rose-500 hover:bg-rose-500/10 rounded p-1.5">
                                            <Trash2 className="w-3 h-3"/>
                                        </button>
                                    </div>
                                    {(actDef?.fields || []).map(f => (
                                        <div key={f.key}>
                                            <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{f.label}</label>
                                            {f.type === "textarea" ? (
                                                <textarea value={act[f.key] ?? act["template"] ?? ""}
                                                    onFocus={() => setFocusedField(`action-${i}-${f.key}`)}
                                                    onChange={e => updateAction(i, f.key, e.target.value)}
                                                    rows={2}
                                                    placeholder={f.default || ""}
                                                    className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-2 py-1.5 rounded-md text-foreground text-xs"/>
                                            ) : f.type === "select" ? (() => {
                                                // Options can be plain strings OR {label, value} objects
                                                const opts = (f.options || []).map(o =>
                                                    typeof o === "object" ? o : { label: o, value: o }
                                                );
                                                const defaultVal = opts[0]?.value ?? "";
                                                return (
                                                    <select value={act[f.key] ?? defaultVal}
                                                        onChange={e => updateAction(i, f.key, e.target.value)}
                                                        className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-2 py-1.5 rounded-md text-foreground text-xs">
                                                        {opts.map(o => (
                                                            <option key={o.value} value={o.value}>{o.label}</option>
                                                        ))}
                                                    </select>
                                                );
                                            })() : (
                                                <input type={f.type === "number" ? "number" : "text"}
                                                    value={act[f.key] ?? f.default ?? ""}
                                                    onFocus={() => setFocusedField(`action-${i}-${f.key}`)}
                                                    onChange={e => updateAction(i, f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                                                    className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-2 py-1.5 rounded-md text-foreground text-xs"/>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                    {/* Placeholder pills — click to insert into the focused field */}
                    {placeholderGroups.length > 0 && (
                        <div className="border-t border-border pt-4" data-testid="builder-placeholders">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-[10px] uppercase tracking-[0.14em] text-primary">Available placeholders</label>
                                {focusedField ? (
                                    <span className="text-[10px] text-emerald-500">Click a chip to insert into the focused field</span>
                                ) : (
                                    <span className="text-[10px] text-muted-foreground">Click into a message/subject field, then click a chip</span>
                                )}
                            </div>
                            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                                {placeholderGroups.map(group => (
                                    <div key={group}>
                                        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">{group}</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(placeholders[group] || []).map(ph => (
                                                <button key={ph.key} type="button"
                                                    onClick={() => insertPlaceholder(ph.key)}
                                                    title={ph.desc}
                                                    data-testid={`placeholder-${ph.key.replace(/[{}]/g, "")}`}
                                                    className="text-[10px] font-mono text-primary bg-primary/5 border border-primary/20 rounded px-1.5 py-0.5 hover:bg-primary/15">
                                                    {ph.key}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="border-t border-border pt-4">
                        <label className="flex items-center gap-2 text-xs">
                            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
                                data-testid="builder-enabled"/>
                            {isEdit ? "Enabled" : "Enable immediately after creating"}
                        </label>
                    </div>
                </div>
                <div className="p-4 border-t border-border flex items-center justify-end gap-2">
                    <button onClick={onClose} data-testid="builder-cancel"
                        className="px-3 py-2 border border-border text-muted-foreground hover:text-foreground rounded-lg text-xs">
                        Cancel
                    </button>
                    <button onClick={submit} data-testid="builder-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-xs uppercase tracking-[0.14em]">
                        <Save className="w-3.5 h-3.5"/> {isEdit ? "Save changes" : "Save rule"}
                    </button>
                </div>
            </div>
        </div>
    );
}
